import React, { useState, useEffect, useContext, useRef } from "react";
import {
  FaUserCircle,
  FaLock,
  FaTimes,
  FaCamera,
  FaEye,
  FaEyeSlash,
  FaBell,
} from "react-icons/fa";
import "../../../assets/css/superadmin/systemSettings/SystemSettings.css";
import MasterLayout from "../../../layout/superadmin/MasterLayout";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";

export default function AdminProfile() {
  const { baseUrl, baseUrlForFile } = useContext(AppContext);
  const fileRef = useRef(null);

  // ── Profile States ──
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phonenumber, setPhonenumber] = useState("");
  const [image, setImage] = useState(null);
  const [avatar, setAvatar] = useState(null);

  // ── UI States ──
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    system: true,
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // ── Fetch Admin Profile ──
  const fetchAdminProfile = () => {
    const token = localStorage.getItem("authToken");
    axios
      .get(`${baseUrl}/superadmin/profile/fetchAdminProfile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          setFirstname(response.data.admin.firstname);
          setLastname(response.data.admin.lastname);
          setPhonenumber(response.data.admin.phonenumber);
          setEmail(response.data.admin.email);
          setImage(response.data.admin.image);
          setNotifications({
            email: response.data.admin.emailNotification == 1,
            system: response.data.admin.inAppNotification == 1,
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  // ── Update Admin Profile ──
  const updateAdminProfile = () => {
    const token = localStorage.getItem("authToken");

    const formData = new FormData();

    formData.append("firstname", firstname);
    formData.append("lastname", lastname);
    formData.append("email", email);
    formData.append("phonenumber", phonenumber);

    if (image instanceof File) {
      formData.append("image", image);
    }

    axios
      .post(`${baseUrl}/superadmin/profile/updateAdminProfile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          fetchAdminProfile();
          setAvatar(null);
          setShowEditModal(false);
        }
      })
      .catch(console.log);
  };

  useEffect(() => {
    fetchAdminProfile();
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImage(file);

    // Preview
    setAvatar(URL.createObjectURL(file));
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  // ── Change Password ──
  const handleSavePassword = (e) => {
    e.preventDefault();

    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword || !newPassword || !confirmPassword) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "Please fill all password fields!" });
      return;
    }
    if (newPassword !== confirmPassword) {
      Swal.fire({ icon: "warning", title: "Mismatch", text: "Passwords do not match!" });
      return;
    }

    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("currentPassword", currentPassword);
    formData.append("newPassword", newPassword);

    axios
      .post(`${baseUrl}/superadmin/profile/changePassword`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          Swal.fire({ icon: "success", title: "Success", text: "Password changed successfully!" });
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setShowPasswordModal(false);
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message || "Failed to change password." });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to change password." });
      });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const toggleNotification = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Change Notification Setting ──
  const changeNotificationSetting = () => {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("emailNotification", notifications.email ? 1 : 0);
    formData.append("inAppNotification", notifications.system ? 1 : 0);

    axios
      .post(`${baseUrl}/superadmin/profile/changeNotificationSetting`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          Swal.fire({ icon: "success", title: "Success", text: "Notification settings updated!" });
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message || "Failed to update notification settings." });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to update notification settings." });
      });
  };

  // ── Skeleton Components ──
  const SkeletonProfileHeader = () => (
    <div className="sadmin-profile-header">
      <div className="sadmin-profile-avatar-section">
        <div className="sadmin-skel sadmin-skel-avatar" />
        <div className="sadmin-profile-info">
          <div className="sadmin-skel sadmin-skel-name" />
          <div className="sadmin-profile-meta">
            <div className="sadmin-skel sadmin-skel-badge" />
            <div className="sadmin-skel sadmin-skel-meta-item" />
          </div>
        </div>
      </div>
      <div className="sadmin-skel sadmin-skel-edit-btn" />
    </div>
  );

  const SkeletonPersonalInfo = () => (
    <div className="sadmin-info-section">
      <div className="sadmin-section-header">
        <div className="sadmin-skel sadmin-skel-icon" />
        <div className="sadmin-skel sadmin-skel-section-title" />
      </div>
      <div className="sadmin-info-grid">
        {[1, 2, 3, 4].map((i) => (
          <div className="sadmin-info-item" key={i}>
            <div className="sadmin-skel sadmin-skel-label" />
            <div className="sadmin-skel sadmin-skel-value" />
          </div>
        ))}
      </div>
    </div>
  );

  const SkeletonSettingsRow = () => (
    <div className="sadmin-settings-row">
      <div className="sadmin-settings-section">
        <div className="sadmin-section-header">
          <div className="sadmin-skel sadmin-skel-icon" />
          <div className="sadmin-skel sadmin-skel-section-title sadmin-skel-section-title--sm" />
        </div>
        <div className="sadmin-security-item">
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div className="sadmin-skel sadmin-skel-sec-label" />
            <div className="sadmin-skel sadmin-skel-sec-sub" />
          </div>
          <div className="sadmin-skel sadmin-skel-change-btn" />
        </div>
      </div>
      <div className="sadmin-settings-section">
        <div className="sadmin-section-header">
          <div className="sadmin-skel sadmin-skel-icon" />
          <div className="sadmin-skel sadmin-skel-section-title sadmin-skel-section-title--xs" />
        </div>
        {[1, 2].map((i) => (
          <div className="sadmin-preference-item" key={i}>
            <div className="sadmin-notif-row">
              <div className="sadmin-notif-info">
                <div className="sadmin-skel sadmin-skel-notif-label" />
                <div className="sadmin-skel sadmin-skel-notif-sub" />
              </div>
              <div className="sadmin-skel sadmin-skel-toggle" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ================================================================
  // RETURN
  // ================================================================

  return (
    <MasterLayout>
      <div className="sadmin-profile-container">
        <div className="sadmin-profile-wrapper">
          {isLoading ? (
            <>
              <SkeletonProfileHeader />
              <div className="sadmin-profile-content">
                <SkeletonPersonalInfo />
                <SkeletonSettingsRow />
              </div>
            </>
          ) : (
            <>
              {/* ── Profile Header ── */}
              <div className="sadmin-profile-header">
                <div className="sadmin-profile-avatar-section">
                  <div className="sadmin-avatar-wrapper">
                    <img
                      src={
                        avatar
                          ? avatar
                          : image
                            ? `${baseUrlForFile}/${image}`
                            : ""
                      }
                      alt="Admin"
                      className="sadmin-profile-avatar"
                    />
                    <button
                      className="sadmin-avatar-badge"
                      onClick={() => fileRef.current?.click()}
                      title="Change photo"
                    >
                      <FaCamera size={11} />
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleAvatarChange}
                    />
                  </div>
                  <div className="sadmin-profile-info">
                    <h2 className="sadmin-profile-name">
                      {firstname} {lastname}
                    </h2>
                    <div className="sadmin-profile-meta">
                      <span className="sadmin-role-badge">Super Admin</span>
                    </div>
                  </div>
                </div>
                <button
                  className="sadmin-edit-profile-btn"
                  onClick={() => setShowEditModal(true)}
                >
                  Edit Profile
                </button>
              </div>

              {/* ── Profile Content ── */}
              <div className="sadmin-profile-content">
                {/* Personal Information */}
                <div className="sadmin-info-section">
                  <div className="sadmin-section-header">
                    <FaUserCircle className="sadmin-section-icon" />
                    <h3 className="sadmin-section-title">
                      Personal Information
                    </h3>
                  </div>
                  <div className="sadmin-info-grid">
                    <div className="sadmin-info-item">
                      <label className="sadmin-info-label">First Name</label>
                      <p className="sadmin-info-value">{firstname}</p>
                    </div>
                    <div className="sadmin-info-item">
                      <label className="sadmin-info-label">Last Name</label>
                      <p className="sadmin-info-value">{lastname}</p>
                    </div>
                    <div className="sadmin-info-item">
                      <label className="sadmin-info-label">Email Address</label>
                      <p className="sadmin-info-value">{email}</p>
                    </div>
                    <div className="sadmin-info-item">
                      <label className="sadmin-info-label">Phone Number</label>
                      <p className="sadmin-info-value">{phonenumber}</p>
                    </div>
                  </div>
                </div>

                <div className="sadmin-settings-row">
                  {/* Account Security */}
                  <div className="sadmin-settings-section">
                    <div className="sadmin-section-header">
                      <FaLock className="sadmin-section-icon" />
                      <h3 className="sadmin-section-title">Account Security</h3>
                    </div>
                    <div className="sadmin-security-item">
                      <div>
                        <p className="sadmin-security-label">
                          Password Management
                        </p>
                        <p className="sadmin-security-sublabel">
                          Update your account password
                        </p>
                      </div>
                      <button
                        className="sadmin-change-btn"
                        onClick={() => setShowPasswordModal(true)}
                      >
                        Change
                      </button>
                    </div>
                  </div>

                  {/* Notifications */}
                  <div className="sadmin-settings-section">
                    <div className="sadmin-section-header">
                      <FaBell className="sadmin-section-icon" />
                      <h3 className="sadmin-section-title">Notifications</h3>
                    </div>
                    <div className="sadmin-preference-item">
                      <div className="sadmin-notif-row">
                        <div className="sadmin-notif-info">
                          <span className="sadmin-notif-label">
                            Email Notifications
                          </span>
                          <span className="sadmin-notif-sub">
                            Receive updates and alerts via email
                          </span>
                        </div>
                        <button
                          className={`sadmin-toggle-btn ${notifications.email ? "sadmin-toggle-on" : ""}`}
                          onClick={() => toggleNotification("email")}
                        >
                          <span className="sadmin-toggle-knob" />
                        </button>
                      </div>
                    </div>
                    <div className="sadmin-preference-item">
                      <div className="sadmin-notif-row">
                        <div className="sadmin-notif-info">
                          <span className="sadmin-notif-label">
                            InApp Notifications
                          </span>
                          <span className="sadmin-notif-sub">
                            In-app alerts for system events
                          </span>
                        </div>
                        <button
                          className={`sadmin-toggle-btn ${notifications.system ? "sadmin-toggle-on" : ""}`}
                          onClick={() => toggleNotification("system")}
                        >
                          <span className="sadmin-toggle-knob" />
                        </button>
                      </div>
                    </div>
                    <button
                      className="sadmin-save-btn"
                      style={{ marginTop: "14px", width: "100%" }}
                      onClick={changeNotificationSetting}
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Edit Profile Modal ── */}
        {showEditModal && (
          <div
            className="sadmin-modal-overlay"
            onClick={() => setShowEditModal(false)}
          >
            <div
              className="sadmin-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sadmin-modal-header">
                <h3 className="sadmin-modal-title">Edit Profile</h3>
                <button
                  className="sadmin-modal-close"
                  onClick={() => setShowEditModal(false)}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="sadmin-modal-body">
                <div className="sadmin-avatar-upload-section">
                  <div className="sadmin-upload-avatar-wrapper">
                    <img
                      src={
                        avatar
                          ? avatar
                          : image
                            ? `${baseUrlForFile}/${image}`
                            : ""
                      }
                      alt="Avatar"
                      className="sadmin-upload-avatar"
                    />
                    <label
                      htmlFor="sadmin-avatar-upload"
                      className="sadmin-upload-btn"
                    >
                      <FaCamera />
                    </label>
                    <input
                      id="sadmin-avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      style={{ display: "none" }}
                    />
                  </div>
                  <p className="sadmin-upload-hint">
                    Click camera icon to change photo
                  </p>
                </div>
                <div className="sadmin-edit-form">
                  <div className="sadmin-form-row">
                    <div className="sadmin-form-field">
                      <label className="sadmin-field-label">First Name</label>
                      <input
                        type="text"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        className="sadmin-field-input"
                      />
                    </div>
                    <div className="sadmin-form-field">
                      <label className="sadmin-field-label">Last Name</label>
                      <input
                        type="text"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        className="sadmin-field-input"
                      />
                    </div>
                  </div>
                  <div className="sadmin-form-field">
                    <label className="sadmin-field-label">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="sadmin-field-input"
                    />
                  </div>
                  <div className="sadmin-form-field">
                    <label className="sadmin-field-label">Phone Number</label>
                    <input
                      type="tel"
                      value={phonenumber}
                      onChange={(e) => setPhonenumber(e.target.value)}
                      className="sadmin-field-input"
                    />
                  </div>
                </div>
              </div>
              <div className="sadmin-modal-footer">
                <button
                  className="sadmin-cancel-btn"
                  onClick={() => {
                    setShowEditModal(false);
                    fetchAdminProfile();
                  }}
                >
                  Cancel
                </button>
                <button
                  className="sadmin-save-btn"
                  onClick={updateAdminProfile}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Change Password Modal ── */}
        {showPasswordModal && (
          <div
            className="sadmin-modal-overlay"
            onClick={() => setShowPasswordModal(false)}
          >
            <div
              className="sadmin-modal-content sadmin-password-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sadmin-modal-header">
                <h3 className="sadmin-modal-title">Change Password</h3>
                <button
                  className="sadmin-modal-close"
                  onClick={() => setShowPasswordModal(false)}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="sadmin-modal-body">
                <div className="sadmin-password-form">
                  {["current", "new", "confirm"].map((field) => (
                    <div className="sadmin-form-field" key={field}>
                      <label className="sadmin-field-label">
                        {field === "current"
                          ? "Current Password"
                          : field === "new"
                            ? "New Password"
                            : "Confirm New Password"}
                      </label>
                      <div className="sadmin-password-input-wrapper">
                        <FaLock className="sadmin-input-icon-left" />
                        <input
                          type={showPasswords[field] ? "text" : "password"}
                          name={
                            field === "current"
                              ? "currentPassword"
                              : field === "new"
                                ? "newPassword"
                                : "confirmPassword"
                          }
                          value={
                            field === "current"
                              ? passwordData.currentPassword
                              : field === "new"
                                ? passwordData.newPassword
                                : passwordData.confirmPassword
                          }
                          onChange={handlePasswordChange}
                          className="sadmin-field-input sadmin-password-input"
                          placeholder={`Enter ${field} password`}
                        />
                        <button
                          type="button"
                          className="sadmin-password-toggle-icon"
                          onClick={() => togglePasswordVisibility(field)}
                        >
                          {showPasswords[field] ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {field === "new" && (
                        <p className="sadmin-password-hint">
                          Must be at least 8 characters long
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="sadmin-modal-footer">
                <button
                  className="sadmin-cancel-btn"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="sadmin-save-btn"
                  onClick={handleSavePassword}
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MasterLayout>
  );
}